import { toast } from "react-toastify";
import { calculateServiceValue, calculateTotalVolumetricWeight } from "../../../utils/estimateUtils";
import { getAlpha2Code } from "../../../lib/helper/countryToAphlaCode";

export const validatePopupFormData = (popupFormData, selectedTemplate, formData) => {
    const errors = {};

    popupFormData.items.forEach((item, index) => {
        if (!item.itemName.trim()) {
            errors[`item_${index}_itemName`] = true;
        }
        if (!item.quantity || isNaN(item.quantity) || parseFloat(item.quantity) < 1) {
            errors[`item_${index}_quantity`] = true;
        }
        if (!item.itemValue || isNaN(item.itemValue) || parseFloat(item.itemValue) <= 0) {
            errors[`item_${index}_itemValue`] = true;
        }
        if (!item.countryOrigin) {
            errors[`item_${index}_countryOrigin`] = true;
        }

        // Enhanced HS Code validation
        if (!item.itemHSCode) {
            errors[`item_${index}_itemHSCode`] = "HS Code is required";
        } else {
            // Enhanced validation for manual entry
            if (item.hsCodeSource === 'manual') {
                const hsCode = item.itemHSCode.toString();
                if (hsCode.length < 6) {
                    errors[`item_${index}_itemHSCode`] = "HS Code must be at least 6 characters long.";
                } else {
                    const first6 = hsCode.substring(0, 6);
                    if (!/^\d{6}$/.test(first6)) {
                        errors[`item_${index}_itemHSCode`] = "First 6 digits of HS Code must be numbers only.";
                    }
                }
            } else {
                // Database validation (existing logic)
                const hsCode = item.itemHSCode.toString();
                if (hsCode.length < 6) {
                    errors[`item_${index}_itemHSCode`] = "HS Code must be at least 6 digits long.";
                }
            }
        }
    });

    if (selectedTemplate !== "EOR" && !["Required", "not_Required"].includes(popupFormData.requiredDoc)) {
        errors['requiredDoc'] = true;
    }
    if (!["YES", "NO"].includes(popupFormData.insuranceSelected)) {
        errors['insuranceSelected'] = true;
    }
    if ((selectedTemplate === "EOR" || selectedTemplate === "EOR & IOR") && !popupFormData.eccnCode) {
        errors['eccnCode'] = true;
    }

    const needsStateSelection = (country) => {
        const countryCode = getAlpha2Code(country);
        return countryCode === 'CA' || countryCode === 'BR';
    };

    if (needsStateSelection(formData.loadCountry) && !formData.loadState) {
        errors['loadState'] = true;
    }
    if (needsStateSelection(formData.unloadCountry) && !formData.unloadState) {
        errors['unloadState'] = true;
    }

    return errors;
};

const getDHLToken = async (setAccessToken, setTokenExpiry) => {
    try {
        const response = await fetch('https://api.dhl.com/auth/v1/token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'client_credentials',
                client_id: '7E2HCWQVR0A10NJKYbYnxRt5uFT7vn1z',
                client_secret: 'uB26UUKHmBMJqbMQ'
            })
        });
        const data = await response.json();
        setAccessToken(data.access_token);
        setTokenExpiry(Date.now() + (15 * 60 * 1000));
        return data.access_token;
    } catch (error) {
        console.error('Error getting DHL token:', error);
        toast.error('Failed to authenticate with DHL');
        return null;
    }
};

const ensureValidToken = async (accessToken, tokenExpiry, setAccessToken, setTokenExpiry) => {
    if (!accessToken || !tokenExpiry || Date.now() >= tokenExpiry) {
        return await getDHLToken(setAccessToken, setTokenExpiry);
    }
    return accessToken;
};

const prepareDHLRequestBody = (items, formData, popupFormData, totalItemsValue, airfreight, totalDimensionsWeight) => {
    return {
        pricingStrategy: "EXACT",
        senderAddress: {
            country: getAlpha2Code(formData.loadCountry),
            state: (getAlpha2Code(formData.loadCountry) === 'CA' || getAlpha2Code(formData.loadCountry) === 'BR') ? (formData.loadState || formData.loadCity || '') : ''
        },
        consigneeAddress: {
            country: getAlpha2Code(formData.unloadCountry),
            state: (getAlpha2Code(formData.unloadCountry) === 'CA' || getAlpha2Code(formData.unloadCountry) === 'BR') ? (formData.unloadState || formData.unloadCity || '') : ''
        },
        packageDetails: {
            outputCurrency: formData.currency,
            transportMode: "AIR",
            endUse: "BUSINESS",
            clearanceMode: "COURIER",
            tax: { included: false, percent: 0, return: true },
            freightCharge: { value: /*parseFloat(airfreight) ||*/ 0, currency: formData.currency },
            insuranceCharge: {
                value: /*popupFormData.insuranceSelected === "YES" ? (totalItemsValue * 0.0075) :*/ 0,
                currency: formData.currency
            }
        },
        customsDetails: items.map(item => ({
            itemId: `ITEM_${Math.random().toString(36).substr(2, 9)}`,
            itemDescription: item.itemName,
            hsCode: String(item.itemHSCode || ''),
            countryOfOrigin: getAlpha2Code(item.countryOrigin),
            itemQuantity: { value: parseInt(item.quantity), unit: "PCS" },
            itemValue: { value: (parseFloat(item.itemValue) * 1.1) || 0, currency: formData.currency },
            weight: { value: totalDimensionsWeight, unit: formData.pcsPackage?.[0]?.weight.unit.toUpperCase() || "KG" }
        }))
    };
};

const filterDHLResponse = (dhlResponse) => {
    if (!dhlResponse) return null;
    const filteredFeeTotals = dhlResponse.feeTotals.filter(fee => fee.value > 0);
    return {
        quoteId: dhlResponse.quoteId,
        pricingStrategy: dhlResponse.pricingStrategy,
        feeTotals: filteredFeeTotals,
        consigneeAddress: dhlResponse.consigneeAddress,
        senderAddress: dhlResponse.senderAddress,
        packageDetails: {
            outputCurrency: dhlResponse.packageDetails.outputCurrency,
            clearanceMode: dhlResponse.packageDetails.clearanceMode,
            transportMode: dhlResponse.packageDetails.transportMode,
            endUse: dhlResponse.packageDetails.endUse,
            freightCharge: dhlResponse.packageDetails.freightCharge,
            insuranceCharge: dhlResponse.packageDetails.insuranceCharge,
        }
    };
};

const getDHLEstimation = async (items, formData, popupFormData, totalItemsValue, airfreight, totalDimensionsWeight, setDuty, setDhlEstimation, accessToken, setAccessToken, tokenExpiry, setTokenExpiry) => {
    const token = await ensureValidToken(accessToken, tokenExpiry, setAccessToken, setTokenExpiry);
    if (!token) return null;

    try {
        const requestBody = prepareDHLRequestBody(items, formData, popupFormData, totalItemsValue, airfreight, totalDimensionsWeight);
        const response = await fetch('https://api.dhl.com/dtc/v1/quotes', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorData = await response.json();
            if (errorData.type?.includes('400.0904002')) {
                const retryBody = { ...requestBody, pricingStrategy: 'MAXIMUM' };
                const retryResponse = await fetch('https://api.dhl.com/dtc/v1/quotes', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(retryBody)
                });

                if (!retryResponse.ok) {
                    throw new Error('Failed to get estimation even with MAXIMUM pricing strategy');
                }

                const retryData = await retryResponse.json();
                const filteredRetryData = filterDHLResponse(retryData);
                const dutyValue = retryData.feeTotals.find(fee => fee.name === 'DUTY')?.value || 0;
                setDuty(dutyValue);
                setDhlEstimation(filteredRetryData);
                return { dhlData: filteredRetryData, dutyValue };
            }
            throw new Error(errorData.message);
        }

        const data = await response.json();
        const filteredData = filterDHLResponse(data);
        const dutyValue = data.feeTotals.find(fee => fee.name === 'DUTY')?.value || 0;
        setDuty(dutyValue);
        setDhlEstimation(filteredData);
        return { dhlData: filteredData, dutyValue };
    } catch (error) {
        return null;
    }
};

export const handleCalculate = async ({
    formData,
    setFormData,
    popupFormData,
    setPopupFormData,
    adminTemplate,
    eccnCodes,
    disabledCountries,
    disabledDutyVatCountries, // New parameter
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    totalItemsQuantity,
    itemsTotalQty,
    setIsPopupOpen,
    setAirfreight,
    setInsurance,
    setCif,
    setDuty,
    setCollectionValue,
    setDhlEstimation,
    setPopupValidationErrors,
    setIsCalculating,
    accessToken,
    setAccessToken,
    tokenExpiry,
    setTokenExpiry
}) => {
    setIsCalculating(true);
    try {
        const errors = validatePopupFormData(popupFormData, formData.selectedTemplate, formData);
        if (Object.keys(errors).length > 0) {
            setPopupValidationErrors(errors);
            toast.error("Please fill in all mandatory fields in the popup.");
            return;
        }

        const isDisabled = disabledCountries.some(country =>
            country.countryName.toLowerCase() === formData.unloadCountry.toLowerCase()
        );
        if (isDisabled) {
            toast.error(`Currently Estimation not Available for ${formData.unloadCountry}. Please contact our team through operations@oneunionsolutions.com with your request.`);
            return;
        }

        // New: Check for disabled Duty & VAT countries
        const isDutyVatDisabled = disabledDutyVatCountries.some(country =>
            country.countryName.toLowerCase() === formData.unloadCountry.toLowerCase()
        );

        setPopupValidationErrors({});

        if (!adminTemplate) {
            toast.error(`Currently Estimation not Available for ${formData.unloadCountry}`);
            return;
        }

        if ((formData.selectedTemplate === "EOR" || formData.selectedTemplate === "EOR & IOR") && !popupFormData.eccnCode) {
            toast.error("ECCN Code is required for EOR or EOR & IOR templates.");
            return;
        }

        if ((formData.selectedTemplate === "EOR" || formData.selectedTemplate === "EOR & IOR") && popupFormData.eccnCode) {
            const selectedEccnObj = eccnCodes.find(eccn => eccn.eccn_code === popupFormData.eccnCode);
            if (selectedEccnObj && selectedEccnObj.restrictions) {
                toast.error("The selected ECCN code is restricted. Please contact the team for assistance.");
                return;
            }
        }

        const dimensions = formData.pcsPackage.map(pkg => ({
            weight: { value: pkg.weight.value || "0", unit: pkg.weight.unit || "kg" },
            length: { value: pkg.length.value || "0", unit: pkg.length.unit || "cm" },
            width: { value: pkg.width.value || "0", unit: pkg.width.unit || "cm" },
            height: { value: pkg.height.value || "0", unit: pkg.height.unit || "cm" },
            quantity: { value: pkg.quantity || "0" }
        }));

        const totalVolumetricWeight = calculateTotalVolumetricWeight(dimensions);
        const volumeWeight = Math.max(totalDimensionsWeight, totalVolumetricWeight);
        let airfreightCharges;
        if (volumeWeight < 360) {
            airfreightCharges = 6.50 * volumeWeight + 250;
        } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
            airfreightCharges = 5.00 * volumeWeight + 250;
        } else {
            airfreightCharges = 4.00 * volumeWeight + 250;
        }
        setAirfreight(airfreightCharges);

        const calculationFormData = {
            ...formData,
            dimensions: dimensions,
            items: popupFormData.items,
            requiredDoc: popupFormData.requiredDoc,
            insuranceSelected: popupFormData.insuranceSelected,
            selectedService: formData.selectedTemplate,
        };

        let dhlData = null;
        let dutyValue = 0;

        if (formData.selectedTemplate !== "EOR" && !isDutyVatDisabled) {
            const result = await getDHLEstimation(
                popupFormData.items,
                formData,
                popupFormData,
                totalItemsValue,
                airfreightCharges,
                totalDimensionsWeight,
                setDuty,
                setDhlEstimation,
                accessToken,
                setAccessToken,
                tokenExpiry,
                setTokenExpiry
            );
            if (!result) {
                return;
            }
            dhlData = result.dhlData;
            dutyValue = result.dutyValue;
        } else if (isDutyVatDisabled) {
            toast.info(`Duty & VAT calculation is disabled for ${formData.unloadCountry}.`);
        }


        setDuty(dutyValue);
        const calculatedInsurance = popupFormData.insuranceSelected === "YES" ? totalItemsValue * 0.0075 : 0;
        setInsurance(calculatedInsurance);

        let collectionValue = 0;
        if (formData.selectedTemplate && adminTemplate?.selectedTemplate?.services) {
            const collectionService = adminTemplate.selectedTemplate.services.find(
                service => service.description.toUpperCase() === "COLLECTION"
            );
            if (collectionService) {
                collectionValue = calculateServiceValue(
                    collectionService,
                    calculationFormData,
                    {},
                    totalItemsValue,
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    0,
                    calculatedInsurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
            }
        }
        setCollectionValue(collectionValue);

        const calculatedCIF = (
            parseFloat(totalItemsValue || 0) +
            parseFloat(airfreightCharges || 0) +
            parseFloat(calculatedInsurance || 0) +
            parseFloat(dutyValue || 0) +
            parseFloat(collectionValue || 0)
        );
        setCif(calculatedCIF);

        let dhlTaxes = [];
        let dhlTaxTotal = 0;
        const specialVATCountries = ['CM', 'HN', 'MD', 'NA', 'SZ', 'EC', 'SC', 'LR', 'IL', 'LB', 'MW', 'GR'];
        const taxConfigs = {
            CM: { rate: 0.175, type: 'VAT' },
            HN: { rate: 0.150, type: 'SALE' },
            SZ: { rate: 0.150, type: 'VAT' },
            EC: { rate: 0.150, type: 'VAT' },
            MD: { rate: 0.200, type: 'VAT' },
            SC: { rate: 0.150, type: 'VAT' },
            LR: { rate: 0.100, type: 'GST' },
            NA: { rate: 0.15, type: 'VAT' },
            IL: { rate: 0.180, type: 'VAT' },
            LB: { rate: 0.110, type: 'VAT' },
            MW: { rate: 0.165, type: 'VAT' },
            GR: { rate: 0.240, type: 'VAT' },
        };

        const calculateSpecialTax = (dutyAmount, CIF, countryCode, totalItemsValue) => {
            const config = taxConfigs[countryCode];
            if (!config) return 0;

            if (countryCode === 'GR') return config.rate * (CIF + dutyAmount);
            if (countryCode === 'LB') {
                const addyFee = dhlData?.feeTotals.find(fee => fee.name === 'ADDY')?.value || 0;
                return config.rate * (dutyAmount + CIF + addyFee);
            }
            if (countryCode === 'NA') return (config.rate * totalItemsValue) * 1.1;
            if (countryCode === 'EC') {
                const relevantFees = ['DUTY', 'FODINFA', 'ICE', 'SAFEGUARD'];
                const feesTotal = dhlData?.feeTotals
                    .filter(fee => relevantFees.includes(fee.name))
                    .reduce((sum, fee) => sum + fee.value, 0) || 0;
                return config.rate * (totalItemsValue + feesTotal);
            }
            if (countryCode === 'SZ') return config.rate * (totalItemsValue + dutyAmount);
            if (countryCode === 'LR') {
                const ecowasLevy = dhlData?.feeTotals.find(fee => fee.name === 'ECOWAS_TRADE_LEVY')?.value || 0;
                return config.rate * (CIF + dutyAmount + ecowasLevy);
            }
            if (countryCode === 'MW') return config.rate * (dutyAmount + CIF);
            return config.rate * (dutyAmount + CIF);
        };

        if (formData.selectedTemplate !== "EOR" && dhlData) {
            const CIF = calculatedCIF;
            const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
            const mainTaxes = {};
            const otherTaxes = [];
            let otherTaxesTotal = 0;
            const countryCode = dhlData.consigneeAddress?.country;
            const itemHsCode = dhlData.customsDetails?.[0]?.hsCode;

            dhlData.feeTotals.forEach((fee) => {
                if (fee.value <= 0) return;
                if (countryCode === 'LB' && fee.name === 'ACD') return;
                const shouldSkipExcise = countryCode === 'MW' && itemHsCode && itemHsCode.startsWith('85');
                if (fee.name === 'EXCISE' && shouldSkipExcise ||
                    (countryCode === 'EC' && fee.name === 'SPCT') ||
                    (countryCode === 'DO' && fee.name === 'CONS') ||
                    (countryCode === 'ET' && fee.name === 'SWL') ||
                    (countryCode === 'IL' && fee.name === 'TAMA')) return;

                if (specificTaxes.includes(fee.name)) {
                    if (specialVATCountries.includes(countryCode) &&
                        (fee.name === 'VAT' || fee.name === 'SALE' || fee.name === 'GST')) {
                        const dutyAmount = dhlData.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
                        fee.value = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
                    }
                    if (fee.value > 0) {
                        mainTaxes[fee.name] = {
                            name: fee.name === 'SALE' || fee.name === 'GST' ? `VAT (${fee.name})` : fee.name,
                            value: fee.value
                        };
                    }
                } else {
                    if (countryCode === 'AU' && fee.name === 'IMPROC') fee.value = CIF * 0.012;
                    if (fee.value > 0) {
                        otherTaxes.push(fee);
                        otherTaxesTotal += fee.value;
                    }
                }
            });

            if (countryCode === 'QA' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
                const extraTaxValue = 400;
                if (extraTaxValue > 0) {
                    otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                    otherTaxesTotal += extraTaxValue;
                }
            }

            if (countryCode === 'TR' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX') &&
                itemHsCode && itemHsCode.startsWith('8')) {
                const extraTaxValue = CIF * 0.05;
                if (extraTaxValue > 0) {
                    otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                    otherTaxesTotal += extraTaxValue;
                }
            }

            if (countryCode === 'AU' && !otherTaxes.some(tax => tax.name === 'IMPROC')) {
                const improcValue = CIF * 0.012;
                if (improcValue > 0) {
                    otherTaxes.push({ name: 'IMPROC', value: improcValue });
                    otherTaxesTotal += improcValue;
                }
            }

            if (countryCode === 'KM' && !otherTaxes.some(tax => tax.name === 'ICF')) {
                const icfValue = CIF * 0.025;
                if (icfValue > 0) {
                    otherTaxes.push({ name: 'ICF', value: icfValue });
                    otherTaxesTotal += icfValue;
                }
            }

            if (countryCode === 'CR' && !otherTaxes.some(tax => tax.name === 'LAW')) {
                const lawValue = CIF * 0.01;
                if (lawValue > 0) {
                    otherTaxes.push({ name: 'LAW', value: lawValue });
                    otherTaxesTotal += lawValue;
                }
            }

            if (countryCode === 'CI' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
                const extraTaxValue = Math.max(CIF * 0.05, 300);
                if (extraTaxValue > 0) {
                    otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                    otherTaxesTotal += extraTaxValue;
                }
            }

            if (countryCode === 'LB' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
                const extraTaxValue = 350;
                if (extraTaxValue > 0) {
                    otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                    otherTaxesTotal += extraTaxValue;
                }
            }

            if (countryCode === 'NA' && !mainTaxes['VAT']) {
                const dutyAmount = dhlData.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
                const vatValue = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
                if (vatValue > 0) mainTaxes['VAT'] = { name: 'VAT', value: vatValue };
            }

            if (countryCode === 'MW' && !mainTaxes['VAT']) {
                const dutyAmount = dhlData.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
                const vatValue = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
                if (vatValue > 0) mainTaxes['VAT'] = { name: 'VAT', value: vatValue };
            }

            specificTaxes.forEach(taxName => {
                if (mainTaxes[taxName] && mainTaxes[taxName].value > 0) {
                    dhlTaxes.push(mainTaxes[taxName]);
                    dhlTaxTotal += mainTaxes[taxName].value;
                }
            });

            if (otherTaxes.length > 0) {
                dhlTaxes.push({ name: 'Taxes', value: otherTaxesTotal, details: otherTaxes });
                dhlTaxTotal += otherTaxesTotal;
            }
        }

        const newCalculatedValues = {};
        if (adminTemplate?.selectedTemplate?.services) {
            adminTemplate.selectedTemplate.services.forEach((service) => {
                newCalculatedValues[service.description] = calculateServiceValue(
                    service,
                    calculationFormData,
                    newCalculatedValues,
                    totalItemsValue,
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    calculatedCIF,
                    calculatedInsurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
            });
        }

        const calculatedServices = adminTemplate.selectedTemplate.services.map(service => ({
            numberOfItem: 1,
            description: service.description,
            exclVAT: newCalculatedValues[service.description] ? newCalculatedValues[service.description].toFixed(2) : "0.00"
        }));

        const dhlServiceItems = dhlTaxes.map(tax => ({
            numberOfItem: 1,
            description: tax.name,
            exclVAT: tax.value.toFixed(2)
        }));

        const allServices = [...calculatedServices, ...dhlServiceItems];
        const estimateTotal = allServices.reduce((sum, service) => sum + (parseFloat(service.exclVAT) || 0), 0);

        setFormData({
            ...formData,
            services: allServices,
            estimateSummary: {
                total: estimateTotal,
                currency: formData.currency,
                services: allServices
            }
        });
        setIsPopupOpen(false);
    } catch (error) {
        console.error("Error during calculation:", error);
        toast.error("An error occurred during calculation. Please try again.");
    } finally {
        setIsCalculating(false);
    }
};